/**
 * 设备列表 —— 数据全部来自 hub 的设备注册表 + 私有网节点。
 *
 * 和原型的两处不同,都是同一条理由(§3 诚实优先):
 *  · 电量/温度/版本来自**设备主动上报的遥测**(hub 的 /devices 顺带给)。
 *    没上报过的设备显示"暂无数据源"角标,而不是一根 0% 的电量条 ——
 *    那会让一队健康设备看着像要没电。
 *  · 批量重启/升级原来往 Supabase 表里 insert 一行就宣布"已下发",而没有任何东西
 *    读那张表。按钮保留但**明着禁用**并标注 P4:装作能用比没有更糟。
 * 能真下发的只有注册表操作:注册、轮换令牌、吊销。
 */
import { useState, useMemo, useCallback } from 'react';
import type { Terminal } from '@/types/fleet';
import { hub, HubError } from '@/lib/hubClient';
import { NA, timeAgo, text, pct, degC } from '@/lib/format';
import { NoSourceTag } from '@/components/feature/DataSource';
import DeviceDetailSheet from './DeviceDetailSheet';

const typeLabels: Record<string, string> = { dog: '机器狗', glasses: 'AI眼镜', phone: '手机', drone: '无人机', vehicle: '无人车', other: '其它' };
const typeIcons: Record<string, string> = { dog: 'ri-android-line', glasses: 'ri-glasses-line', phone: 'ri-smartphone-line', drone: 'ri-flight-takeoff-line', vehicle: 'ri-car-line', other: 'ri-device-line' };

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) return '"' + value.replace(/"/g, '""') + '"';
  return value;
}

function exportToCsv(terminals: Terminal[], fileName: string) {
  const headers = ['标识', '名称', '类型', '状态', '私有网IP', '电量%', '温度℃', '版本', '遥测时间', '最后在线', '已吊销'];
  const rows = terminals.map((t) => [
    escapeCsvField(t.id), escapeCsvField(t.name), typeLabels[t.type] || t.type,
    t.status === 'online' ? '在线' : '离线', text(t.overlayIP),
    // 导出里也不能把"没上报"写成 0 —— 那份 CSV 会被拿去做统计
    t.battery == null ? '' : String(t.battery),
    t.temperature == null ? '' : String(t.temperature),
    text(t.version), text(t.telemetryAt ?? null), text(t.lastSeen),
    t.revoked ? '是' : '否',
  ]);
  const bom = '﻿';
  const blob = new Blob([bom + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a'); link.href = url; link.download = fileName;
  document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
}

type SortMode = 'default' | 'seen-desc' | 'name-asc';
const sortLabels: Record<SortMode, string> = { default: '默认排序', 'seen-desc': '最近在线 ↓', 'name-asc': '名称 A→Z' };

interface DeviceListProps { terminals: Terminal[]; onRegisterClick: () => void; onChanged: () => void; }

export default function DeviceList({ terminals, onRegisterClick, onChanged }: DeviceListProps) {
  const [selectedDevice, setSelectedDevice] = useState<Terminal | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortMode>('default');
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = terminals.filter((t) => {
      const matchSearch = !q || t.id.toLowerCase().includes(q) || t.name.toLowerCase().includes(q)
        || (typeLabels[t.type] ?? '').includes(search) || (t.overlayIP ?? '').includes(q);
      const matchType = typeFilter === 'all' || t.type === typeFilter;
      const matchStatus = statusFilter === 'all' || t.status === statusFilter;
      return matchSearch && matchType && matchStatus;
    });
    if (sortBy === 'seen-desc') {
      // 从未上线过(lastSeen=null)排最后 —— 不是"最久没上线", 是没有过。
      result = [...result].sort((a, b) => (b.lastSeen ? Date.parse(b.lastSeen) : -1) - (a.lastSeen ? Date.parse(a.lastSeen) : -1));
    } else if (sortBy === 'name-asc') {
      result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'zh'));
    }
    return result;
  }, [terminals, search, typeFilter, statusFilter, sortBy]);

  const allChecked = filtered.length > 0 && filtered.every((t) => checkedIds.has(t.id));
  const toggleCheck = (id: string) => setCheckedIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setCheckedIds(allChecked ? new Set() : new Set(filtered.map((t) => t.id)));

  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(null), 2500); };

  const doRevoke = async () => {
    setBusy(true); setError(null);
    const ids = [...checkedIds];
    try {
      for (const id of ids) await hub.revokeDevice(id);
      flash(`已吊销 ${ids.length} 台终端的令牌`);
      setCheckedIds(new Set()); setConfirmRevoke(false); onChanged();
    } catch (e) {
      setError(e instanceof HubError && e.status === 403 ? '仅座舱(admin 令牌)可吊销设备' : e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const handleExport = useCallback(() => {
    if (filtered.length === 0) return;
    const now = new Date();
    const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
    exportToCsv(filtered, `终端列表-${ts}.csv`);
    flash(`已导出 ${filtered.length} 条数据`);
  }, [filtered]);

  const StatusDot = ({ t }: { t: Terminal }) => (
    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${t.revoked ? 'bg-red-400' : t.status === 'online' ? 'bg-accent-500' : 'bg-foreground-300'}`} />
  );

  return (
    <>
      {/* 详情打开时, 宽屏(xl+)把主内容右侧让出抽屉的宽度(480px)+间距, 于是两边【并排】而不是被盖住。
          窄屏不让(抽屉在那儿是全屏模态, 让了反而挤没了)。见 DeviceDetailSheet 的遮罩 xl:hidden。 */}
      <div className={`transition-[padding] duration-200 ${selectedDevice ? 'xl:pr-[496px]' : ''}`}>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mb-4">
        <div className="relative flex-1 sm:max-w-xs">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400"><i className="ri-search-line text-sm" /></span>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="搜索标识、名称、类型、IP..." className="w-full pl-9 pr-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all" />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-0.5">
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setCheckedIds(new Set()); }} className="px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"><option value="all">全部类型</option>{Object.entries(typeLabels).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}</select>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCheckedIds(new Set()); }} className="px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"><option value="all">全部状态</option><option value="online">在线</option><option value="offline">离线</option></select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortMode)} className="px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer">{Object.entries(sortLabels).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}</select>
          <button onClick={handleExport} disabled={filtered.length === 0} className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-lg border border-background-200/70 text-foreground-500 bg-background-50 hover:text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"><i className="ri-download-line text-sm" />导出</button>
          <button onClick={onRegisterClick} className="inline-flex items-center gap-1.5 px-3 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium rounded-lg hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap btn-lift"><i className="ri-add-line text-sm" />注册终端</button>
        </div>
      </div>

      {error && <p className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200/70 rounded-lg px-3 py-2">{error}</p>}
      {toast && <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-foreground-800 text-background-50 text-xs rounded-lg shadow-lg animate-fade-in-up pointer-events-none">{toast}</div>}

      {/* 桌面/平板:表格,横向滚动包在自己的容器里,页面本身不横滚(§6b) */}
      <div className="hidden md:block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-100"><tr>
              <th className="w-10 px-3 py-2.5"><input type="checkbox" checked={allChecked} onChange={toggleAll} className="w-4 h-4 rounded border-background-300 text-primary-500 cursor-pointer" /></th>
              {['名称', '类型', '状态', '私有网 IP', '遥测', '最后在线', '操作'].map((h) => (
                <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-foreground-500 whitespace-nowrap">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-background-200/50">
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-16">
                  <div className="flex flex-col items-center">
                    <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 mb-3"><i className="ri-device-line text-2xl text-foreground-300" /></span>
                    <p className="text-sm font-medium text-foreground-500">{terminals.length === 0 ? '注册表里还没有终端' : '无匹配结果'}</p>
                    <p className="text-xs text-foreground-400 mt-1">{terminals.length === 0 ? '点击「注册终端」为第一台设备生成接入令牌' : '尝试调整筛选条件'}</p>
                  </div>
                </td></tr>
              ) : filtered.map((t) => (
                <tr key={t.id} className={`transition-all duration-200 ${t.status === 'online' ? 'hover:bg-background-100' : 'opacity-70 hover:opacity-90'} ${checkedIds.has(t.id) ? 'bg-primary-50/50' : ''}`}>
                  <td className="px-3 py-3"><input type="checkbox" checked={checkedIds.has(t.id)} onChange={() => toggleCheck(t.id)} className="w-4 h-4 rounded border-background-300 text-primary-500 cursor-pointer" /></td>
                  <td className="px-3 py-3 cursor-pointer" onClick={() => setSelectedDevice(t)}>
                    <div className="flex items-center gap-2"><StatusDot t={t} /><div><span className="font-medium text-foreground-800">{t.name}</span><span className="block text-[10px] font-mono text-foreground-400">{t.id}</span></div></div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap"><span className="inline-flex items-center gap-1 text-xs text-foreground-500"><i className={`${typeIcons[t.type]} text-sm`} />{typeLabels[t.type]}</span></td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {t.revoked ? <span className="text-xs font-medium text-red-600">已吊销</span>
                      : <span className={`text-xs font-medium ${t.status === 'online' ? 'text-accent-600' : 'text-foreground-400'}`}>{t.status === 'online' ? '在线' : '离线'}</span>}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap"><span className="text-xs font-mono text-foreground-500">{text(t.overlayIP)}</span></td>
                  <td className="px-3 py-3">
                    {t.telemetryAt ? (
                      <span className="flex items-center gap-2 text-xs font-mono whitespace-nowrap">
                        <span className={t.battery != null && t.battery <= 10 ? 'text-red-600' : 'text-foreground-600'}>{pct(t.battery)}</span>
                        <span className={t.temperature != null && t.temperature >= 70 ? 'text-amber-600' : 'text-foreground-400'}>{degC(t.temperature)}</span>
                        <span className="text-foreground-400 truncate max-w-[70px]">{t.version ?? ''}</span>
                      </span>
                    ) : (
                      <NoSourceTag hint="这台设备还没上报过遥测(POST /api/v1/devices/telemetry)" />
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap"><span className="text-xs font-mono text-foreground-400">{timeAgo(t.lastSeen)}</span></td>
                  <td className="px-3 py-3"><button onClick={() => setSelectedDevice(t)} className="w-9 h-9 flex items-center justify-center rounded hover:bg-background-200 transition-colors cursor-pointer"><i className="ri-eye-line text-sm text-foreground-500" /></button></td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 手机:表格转卡片(§6b) */}
      <div className="md:hidden space-y-2">
        {filtered.length === 0 ? (
          <div className="bg-background-50 border border-background-200/70 rounded-xl py-12 text-center">
            <p className="text-sm font-medium text-foreground-500">{terminals.length === 0 ? '注册表里还没有终端' : '无匹配结果'}</p>
          </div>
        ) : filtered.map((t) => (
          <div key={t.id} onClick={() => setSelectedDevice(t)} className="bg-background-50 border border-background-200/70 rounded-xl p-3 active:bg-background-100">
            <div className="flex items-start gap-2">
              <input type="checkbox" checked={checkedIds.has(t.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleCheck(t.id)} className="w-5 h-5 mt-0.5 rounded border-background-300 text-primary-500 cursor-pointer" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><StatusDot t={t} /><span className="font-medium text-sm text-foreground-800 truncate">{t.name}</span>
                  <span className="ml-auto text-[11px] shrink-0">{t.revoked ? <span className="text-red-600 font-medium">已吊销</span> : <span className={t.status === 'online' ? 'text-accent-600' : 'text-foreground-400'}>{t.status === 'online' ? '在线' : '离线'}</span>}</span>
                </div>
                <p className="text-[11px] font-mono text-foreground-400 mt-0.5 truncate">{t.id}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-foreground-500">
                  <span className="inline-flex items-center gap-1"><i className={`${typeIcons[t.type]} text-xs`} />{typeLabels[t.type]}</span>
                  <span className="font-mono">{text(t.overlayIP)}</span>
                  <span className="font-mono text-foreground-400">{timeAgo(t.lastSeen)}</span>
                </div>
                <div className="mt-2">
                  {t.telemetryAt ? (
                    <span className="text-[11px] font-mono text-foreground-500">{pct(t.battery)} · {degC(t.temperature)}{t.version ? ` · ${t.version}` : ''}</span>
                  ) : <NoSourceTag hint="这台设备还没上报过遥测" />}
                </div>
              </div>
            </div>
          </div>))}
      </div>

      <p className="text-[11px] text-foreground-400 mt-2">共 {filtered.length} 台终端{search && ` · 搜索 "${search}"`}{typeFilter !== 'all' && ` · ${typeLabels[typeFilter]}`}{statusFilter !== 'all' && ` · ${statusFilter === 'online' ? '在线' : '离线'}`}</p>

      {checkedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 flex justify-center pointer-events-none pb-[max(1rem,env(safe-area-inset-bottom))] px-3">
          <div className="pointer-events-auto animate-batch-bar-in flex flex-wrap items-center justify-center gap-2 sm:gap-3 px-4 py-3 bg-background-800 text-background-50 rounded-2xl shadow-lg max-w-full">
            <span className="text-xs text-background-300">已选 <strong className="text-background-50">{checkedIds.size}</strong> 台</span>
            <div className="w-px h-4 bg-background-600 hidden sm:block" />
            <button onClick={() => setConfirmRevoke(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 text-xs font-medium cursor-pointer whitespace-nowrap"><i className="ri-forbid-line text-sm" />吊销令牌</button>
            <button disabled title="OTA 基础设施尚不存在(P4)" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-700 text-background-400 text-xs font-medium cursor-not-allowed whitespace-nowrap"><i className="ri-upload-cloud-line text-sm" />批量升级 · P4</button>
            <button onClick={() => setCheckedIds(new Set())} className="text-xs text-background-400 px-2 py-2 cursor-pointer whitespace-nowrap">取消</button>
          </div>
        </div>
      )}

      {confirmRevoke && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-backdrop-in" style={{ background: 'oklch(0.12 0.005 90 / 0.4)' }}>
          <div className="bg-background-50 rounded-xl border border-background-200/70 max-w-md w-full p-6 animate-fade-in-up shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-100 text-red-600"><i className="ri-forbid-line text-lg" /></span>
              <div><h3 className="text-sm font-bold text-foreground-900">吊销 {checkedIds.size} 台终端的令牌</h3><p className="text-xs text-foreground-500">这些设备将立即无法调用 hub。恢复要重新 rotate 一把令牌并填回设备。</p></div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmRevoke(false)} disabled={busy} className="px-4 py-2 text-xs font-medium text-foreground-600 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={doRevoke} disabled={busy} className="px-4 py-2 rounded-lg text-xs font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-60 cursor-pointer whitespace-nowrap">{busy ? '执行中…' : '确认吊销'}</button>
            </div>
          </div>
        </div>
      )}

      </div>

      {selectedDevice && <DeviceDetailSheet device={selectedDevice} onClose={() => setSelectedDevice(null)} onChanged={onChanged} />}
    </>
  );
}
